import { ModalProvider, ModalType, useModals } from '@frontend/modal';
import React, { MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { getReactiveDetailRegistryEntry } from '../reactive-detail-shell/reactive-detail-registry';
import { ReactiveDetailShell } from '../reactive-detail-shell/reactive-detail-shell.component';
import {
    PlatformDetailEntry,
    createPlatformDetailEntry,
    parsePlatformDetailStack,
    platformDetailKey,
    pushPlatformDetailEntry,
    removePlatformDetailEntry,
    updatePlatformDetailTab,
    writePlatformDetailStack
} from './platform-detail-url';
import { PlatformModalHostContext } from './platform-modal-host.context';
import type { PlatformModalHostApi } from './platform-modal-host.types';

export { usePlatformModalHost } from './platform-modal-host.context';
export type { OpenPlatformDetailOptions, OpenPlatformModalOptions, PlatformModalHostApi } from './platform-modal-host.types';

const DEFAULT_DETAIL_PANE_CLASS_NAME = 'w-full xl:w-1/2 max-w-[900px]';

/**
 * Platform modal host. Owns the ModalProvider so every modal it renders is inside the host context.
 *
 * Detail panes are mirrored in `?platform_details=[...]`; ephemeral modals never touch the URL.
 */
export const PlatformModalHost = (props: { children: React.ReactNode }) => {
    const implementationRef = useRef<PlatformModalHostApi | null>(null);

    const api = useMemo<PlatformModalHostApi>(() => {
        const current = () => {
            if (!implementationRef.current) {
                throw new Error('PlatformModalHost is not mounted');
            }
            return implementationRef.current;
        };
        return {
            openPlatformDetail: (options) => current().openPlatformDetail(options),
            openPlatformModal: (options) => current().openPlatformModal(options),
            setPlatformDetailTab: (target, tab, dropdown) => current().setPlatformDetailTab(target, tab, dropdown)
        };
    }, []);

    // ModalProvider resolves its portal root in an effect, which runs after child effects.
    // Mount the stack sync one commit later so panes restored from a pasted URL can open.
    const [modalProviderMounted, setModalProviderMounted] = useState(false);
    useEffect(() => setModalProviderMounted(true), []);

    return (
        <PlatformModalHostContext.Provider value={api}>
            <ModalProvider>
                {props.children}
                {modalProviderMounted && <PlatformDetailStack implementationRef={implementationRef} />}
            </ModalProvider>
        </PlatformModalHostContext.Provider>
    );
};

/** Keeps the ModalProvider display stack and `platform_details` in sync. */
const PlatformDetailStack = (props: { implementationRef: MutableRefObject<PlatformModalHostApi | null> }) => {
    const { openModal, closeModal } = useModals();
    const navigate = useNavigate();
    const location = useLocation();

    // Pane callbacks outlive the render that created them, so router state is read through a ref.
    // `search` is also bumped right after navigate so back-to-back writes build on each other.
    const routerRef = useRef({ navigate, search: location.search });
    routerRef.current = { navigate, search: location.search };

    const openPanesRef = useRef(new Map<string, string>());
    const paneCounterRef = useRef(0);

    const writeStack = useCallback((update: (stack: PlatformDetailEntry[]) => PlatformDetailEntry[], replace: boolean) => {
        const search = new URLSearchParams(routerRef.current.search);
        writePlatformDetailStack(search, update(parsePlatformDetailStack(search)));
        const nextSearch = search.toString();
        routerRef.current.search = nextSearch ? `?${nextSearch}` : '';
        routerRef.current.navigate({ search: nextSearch }, { replace });
    }, []);

    const openPane = useCallback(
        (entry: PlatformDetailEntry) => {
            const key = platformDetailKey(entry);
            const previousModalId = openPanesRef.current.get(key);
            if (previousModalId) {
                closeModal(previousModalId);
            }

            // A fresh id per open: ModalProvider updates an existing id in place instead of moving it to the top.
            paneCounterRef.current += 1;
            const modalId = `platform-detail:${key}:${paneCounterRef.current}`;
            openPanesRef.current.set(key, modalId);

            openModal(
                modalId,
                () => <ReactiveDetailShell detail={entry} />,
                {
                    backdropDismissible: true,
                    handleCloseCallback: () => {
                        if (openPanesRef.current.get(key) !== modalId) {
                            return;
                        }
                        openPanesRef.current.delete(key);
                        writeStack((stack) => removePlatformDetailEntry(stack, key), true);
                    }
                },
                {
                    type: ModalType.PANE,
                    className: getReactiveDetailRegistryEntry(entry.type)?.modalClassName ?? DEFAULT_DETAIL_PANE_CLASS_NAME
                }
            );
        },
        [closeModal, openModal, writeStack]
    );

    useEffect(() => {
        const stack = parsePlatformDetailStack(location.search);
        const keys = new Set(stack.map(platformDetailKey));

        openPanesRef.current.forEach((modalId, key) => {
            if (!keys.has(key)) {
                closeModal(modalId);
                openPanesRef.current.delete(key);
            }
        });
        stack.forEach((entry) => {
            if (!openPanesRef.current.has(platformDetailKey(entry))) {
                openPane(entry);
            }
        });
    }, [location.search, closeModal, openPane]);

    props.implementationRef.current = {
        openPlatformDetail: (options) => {
            const entry = createPlatformDetailEntry({ ...options, type: options.entityType });
            if (!entry) {
                return;
            }
            // Always re-open: URL "top" is not display top when an ephemeral modal sits above the pane.
            openPane(entry);
            writeStack((current) => pushPlatformDetailEntry(current, entry), false);
        },
        openPlatformModal: (options) => {
            openModal(options.id, options.render, options.wrapperOptions, options.modalOptions);
        },
        setPlatformDetailTab: (target, tab, dropdown) => {
            const key = platformDetailKey({ type: target.entityType, id: target.id });
            writeStack((stack) => updatePlatformDetailTab(stack, key, tab, dropdown), true);
        }
    };

    return null;
};
